import { useCallback, useEffect, useRef, useState } from 'react';
import type { BrowserQRCodeReader } from '@zxing/browser';

/**
 * useQrScanner — wraps @zxing/browser for continuous camera QR decoding.
 *
 * Manages camera lifecycle: requests permission, streams video into a <video>
 * ref, and decodes QR codes as they appear in the frame. Automatically stops
 * on unmount to release the camera.
 *
 * Uses the synchronous `decode(videoElement)` method in a polling loop
 * (setTimeout every 200ms) to capture individual frames and attempt QR
 * decoding. This avoids the blocking behaviour of `decodeOnceFromVideoElement`
 * which has its own internal retry loop that never yields control back.
 *
 * Requirements: guard-web-app §8 (QR validation)
 */

export type ScannerState =
  | 'idle'
  | 'requesting-permission'
  | 'scanning'
  | 'paused'
  | 'error-permission'
  | 'error-no-camera'
  | 'error-generic';

export interface UseQrScannerResult {
  /** Attach this to the <video> element the scanner streams into. */
  videoRef: React.RefObject<HTMLVideoElement | null>;
  state: ScannerState;
  /** Last successfully decoded QR token value. Null when nothing decoded yet. */
  lastResult: string | null;
  /** Start (or resume) scanning. */
  start: () => Promise<void>;
  /** Pause decoding without stopping the camera stream. */
  pause: () => void;
  /** Stop the camera and release all resources. */
  stop: () => void;
  /** Reset state and last result — call after processing a scan. */
  reset: () => void;
  errorMessage: string | null;
}

export function useQrScanner(onDecode: (token: string) => void): UseQrScannerResult {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [state, setState] = useState<ScannerState>('idle');
  const [lastResult, setLastResult] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const streamRef = useRef<MediaStream | null>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isPausedRef = useRef(false);
  const onDecodeRef = useRef(onDecode);
  onDecodeRef.current = onDecode;

  // BrowserQRCodeReader instance — created once, reused across decode ticks.
  // We use the synchronous `decode(element)` method which captures a single
  // frame and attempts to decode it, throwing NotFoundException if no QR is
  // visible. This gives us full control over the polling loop.
  const decoderRef = useRef<BrowserQRCodeReader | null>(null);

  const stopStream = useCallback(() => {
    if (timeoutRef.current != null) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
  }, []);

  const runDecodeLoop = useCallback(() => {
    // Idempotent: clear any in-flight loop before starting a fresh one so that
    // re-entry (resume after pause, or the attach effect re-firing) never leaves
    // two concurrent polling chains running against the same video element.
    if (timeoutRef.current != null) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    const decode = () => {
      if (isPausedRef.current || !videoRef.current || !decoderRef.current) return;

      // Only attempt decode when the video has valid frame data
      const video = videoRef.current;
      if (video.readyState < 2 || video.videoWidth === 0 || video.videoHeight === 0) {
        // Video not ready yet — retry shortly
        timeoutRef.current = setTimeout(decode, 200);
        return;
      }

      try {
        // Synchronous single-frame decode: captures the current video frame
        // into a canvas and attempts QR decoding. Throws NotFoundException
        // when no QR code is visible — expected, we just retry.
        const result = decoderRef.current.decode(video);
        const token = result.getText();
        if (token) {
          setLastResult(token);
          isPausedRef.current = true;
          setState('paused');
          onDecodeRef.current(token);
          return;
        }
      } catch {
        // NotFoundException when no QR in frame — expected, continue polling
      }

      // Schedule next decode attempt
      timeoutRef.current = setTimeout(decode, 200);
    };
    decode();
  }, []);

  const start = useCallback(async () => {
    // Guard against concurrent calls — both 'scanning' and 'requesting-permission'
    // must be blocked. Without the latter, a double-tap opens two getUserMedia
    // calls simultaneously; whichever resolves second overwrites streamRef,
    // leaking the first stream's tracks permanently.
    if (state === 'scanning' || state === 'requesting-permission') return;

    setState('requesting-permission');
    setErrorMessage(null);
    isPausedRef.current = false;

    try {
      // Check for secure context — getUserMedia requires HTTPS or localhost
      if (!navigator.mediaDevices?.getUserMedia) {
        setState('error-generic');
        setErrorMessage(
          'Camera access requires a secure connection (HTTPS). Please access this page over HTTPS or on localhost.',
        );
        return;
      }

      if (!decoderRef.current) {
        const { BrowserQRCodeReader } = await import('@zxing/browser');
        decoderRef.current = new BrowserQRCodeReader();
      }

      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: { ideal: 'environment' },
          width: { ideal: 1280 },
          height: { ideal: 720 },
        },
        audio: false,
      });

      streamRef.current = stream;

      // Transition to 'scanning' so the <video> element mounts. The stream is
      // attached to that element in the effect below — it cannot be attached
      // here because the <video> is not rendered during 'requesting-permission',
      // so videoRef.current is still null at this point.
      setState('scanning');
    } catch (err: unknown) {
      stopStream();
      const error = err as { name?: string; message?: string };
      if (error?.name === 'NotAllowedError' || error?.name === 'PermissionDeniedError') {
        setState('error-permission');
        setErrorMessage(
          'Camera access denied. Please allow camera access in your browser settings.',
        );
      } else if (error?.name === 'NotFoundError' || error?.name === 'DevicesNotFoundError') {
        setState('error-no-camera');
        setErrorMessage('No camera found on this device.');
      } else {
        setState('error-generic');
        setErrorMessage(error?.message ?? 'An error occurred starting the camera.');
      }
    }
  }, [state, stopStream]);

  // Attach the acquired camera stream to the <video> element once it is mounted.
  // start() flips state to 'scanning' first, which renders the <video>; this
  // effect then runs post-commit (element guaranteed in the DOM) and wires the
  // stream in, then begins decoding. Splitting acquisition from attachment fixes
  // the race where getUserMedia resolves before the element exists.
  useEffect(() => {
    if (state !== 'scanning') return;
    const video = videoRef.current;
    const stream = streamRef.current;
    if (!video || !stream) return;
    if (video.srcObject !== stream) {
      video.srcObject = stream;
      // play() may reject if the element is detached mid-play; the decode loop
      // tolerates a not-yet-playing video via its readyState guard, so ignore.
      void video.play().catch(() => undefined);
    }
    runDecodeLoop();
  }, [state, runDecodeLoop]);

  const pause = useCallback(() => {
    isPausedRef.current = true;
    if (timeoutRef.current != null) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    setState('paused');
  }, []);

  const stop = useCallback(() => {
    stopStream();
    isPausedRef.current = false;
    setState('idle');
  }, [stopStream]);

  const reset = useCallback(() => {
    isPausedRef.current = false;
    setLastResult(null);
    setErrorMessage(null);
    // The attach effect resumes the decode loop when state becomes 'scanning'.
    setState(streamRef.current ? 'scanning' : 'idle');
  }, []);

  useEffect(() => {
    return () => {
      stopStream();
    };
  }, [stopStream]);

  return { videoRef, state, lastResult, start, pause, stop, reset, errorMessage };
}
